import React, { useState, useEffect } from 'react';

const DEFAULT_ACCENT = '#749555';

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(116, 149, 85, ${alpha})`;
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map(char => char + char).join('');
  }
  if (normalized.length !== 6) {
    return `rgba(116, 149, 85, ${alpha})`;
  }

  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const LinkBlock = ({
  icon,
  title,
  isExpanded,
  onClick,
  children,
  className = '',
  isExternalLink = false,
  isCustom = false,
  hideTitle = false,
  accentColor = DEFAULT_ACCENT,
  transparentBg = false,
  isTopSection = false
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const safeAccent = accentColor || DEFAULT_ACCENT;
  const expandedHeaderStyle = isExpanded
    ? {
        background: `linear-gradient(to bottom, ${safeAccent} 0%, #1a1a1a 100%)`,
        borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
      }
    : isTopSection
      ? {
          background: 'transparent'
        }
      : {};
  const containerStyle = {
    borderColor: safeAccent
  };
  const iconWrapperStyle = isExpanded
    ? { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
    : isTopSection
      ? { backgroundColor: hexToRgba(safeAccent, 0.3) }
      : { backgroundColor: hexToRgba(safeAccent, 0.15) };
  const iconColorStyle = {
    color: isExpanded ? '#ffffff' : safeAccent
  };
  const chevronStyle = {
    color: isExpanded ? '#ffffff' : safeAccent
  };
  const parentStyle = isTopSection
    ? { boxShadow: `0 0 15px ${hexToRgba(safeAccent, 0.5)}` }
    : {};
  return (
    <div className={`w-full max-w-2xl mx-auto ${isTopSection ? 'mt-12 mb-12 rounded-lg' : ''} ${className}`} style={parentStyle}>
      <div className={`${transparentBg ? 'bg-transparent' : 'bg-gray-900'} border-2 rounded-lg transition-all duration-200 overflow-hidden ${
        isExpanded ? 'shadow-lg' : 'shadow-sm hover:shadow-md'
      }`} style={containerStyle}>
        {/* Header Section */}
        <button
          onClick={onClick}
          className={`w-full ${
            isTopSection && !isExpanded
              ? (isMobile ? 'p-6' : 'p-8')
              : (isMobile ? 'p-3' : 'p-4')
          } transition-colors duration-200 ${
            isExpanded
              ? 'hover:opacity-95 rounded-t-lg'
              : 'hover:bg-gray-800 rounded-lg'
          }`}
          style={expandedHeaderStyle}
        >
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              {icon && (
                <div
                  className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} rounded-full flex items-center justify-center flex-shrink-0`}
                  style={iconWrapperStyle}
                >
                  <i
                    className={`fas ${icon} ${isMobile ? 'text-lg' : 'text-xl'} ${
                      isExpanded ? '' : ''
                    }`}
                    style={iconColorStyle}
                  ></i>
                </div>
              )}
              {!hideTitle && (
                <h3
                  className={`${
                    isTopSection
                      ? (isMobile ? 'text-lg' : 'text-2xl')
                      : (isMobile ? 'text-sm' : 'text-lg')
                  } font-semibold leading-tight text-left min-w-0 flex-1 ${
                    isExpanded ? 'text-white' : 'text-gray-100'
                  }`}
                  style={isTopSection && !isExpanded ? { color: safeAccent } : {}}
                >
                  {title}
                </h3>
              )}
            </div>
            {isExternalLink ? (
              <i
                className="fas fa-external-link-alt text-lg"
                style={{ color: isTopSection ? safeAccent : '#ef4444' }}
              ></i>
            ) : (
              <i
                className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} transition-transform duration-200 ${
                  isExpanded ? '' : ''
                }`}
                style={chevronStyle}
              ></i>
            )}
          </div>
        </button>

        {/* Expandable Content */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkBlock;
